import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import DesignCTA from "@/components/DesignCTA";
import { IMAGES } from "@/data/content";

const FOCUS = [
  {
    title: "Ice Hockey Specialization",
    desc: "Hockey is at the heart of our range — jerseys, socks, shells, teamwear and training apparel for the sport we know best.",
  },
  {
    title: "Custom Production",
    desc: "Everything we make follows your design — colors, cresting and sizing defined by you, not by a catalog.",
  },
  {
    title: "Teams, Clubs & Trade",
    desc: "We work with teams, clubs, sports organizations, retailers and distributors around the world.",
  },
];

const WORKFLOW = [
  "You provide your design, logo, colors, specifications or references",
  "We discuss products, quantities and requirements together",
  "We prepare a design/mockup for your approval",
  "After approval, your order moves into manufacturing",
];

export default function About() {
  return (
    <>
      <PageHero
        testId="about-hero"
        eyebrow="About Us"
        title="Top Player Sportswear"
        sub="A sportswear manufacturer based in Sialkot, Pakistan — with ice hockey at the core and a range across sports."
        img={IMAGES.factoryLine}
      />

      <section className="py-14 md:py-20" data-testid="about-intro">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-10 items-start">
          <Reveal>
            <div>
              <p className="eyebrow mb-3">Who we are</p>
              <h2 className="h2">Your designs, our production</h2>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="space-y-4 text-base text-slate-600 leading-relaxed">
              <p>
                Top Player Sportswear is a sportswear manufacturer based in Sialkot,
                Pakistan, specializing in custom teamwear and sportswear. With a strong
                focus on ice hockey, we also manufacture apparel for a wide range of sports
                and athletic activities.
              </p>
              <p>
                Customers come to us with their own designs, logos, colors and requirements —
                and every garment we produce is made for that specific customer.
              </p>
              <p>
                Before anything is manufactured, we prepare a design/mockup for approval, so
                you always know exactly what your team will wear.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="pb-14 md:pb-20" data-testid="about-focus">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-3 gap-6">
          {FOCUS.map((f, i) => (
            <Reveal key={f.title} delay={i * 0.06}>
              <div className="h-full bg-white rounded-lg border border-slate-200 p-7 hover:border-ice/60 hover:shadow-md transition-all duration-300" data-testid={`about-focus-${i}`}>
                <span className="block h-1 w-10 bg-ice rounded-full mb-5" />
                <h3 className="font-display font-semibold uppercase text-xl text-navy">{f.title}</h3>
                <p className="mt-3 text-sm text-slate-600 leading-relaxed">{f.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="pb-14 md:pb-20" data-testid="about-workflow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="bg-slate-50 rounded-lg border border-slate-200 p-8 md:p-10">
              <p className="eyebrow mb-3">How we work with you</p>
              <ol className="mt-5 space-y-4">
                {WORKFLOW.map((w, i) => (
                  <li key={w} className="flex items-start gap-4">
                    <span className="font-display font-bold text-xl text-ice shrink-0">{String(i + 1).padStart(2, "0")}</span>
                    <span className="text-sm sm:text-base text-slate-600 leading-relaxed">{w}</span>
                  </li>
                ))}
              </ol>
              <Link to="/manufacturing" data-testid="about-manufacturing-link" className="mt-8 link-accent">
                More about our manufacturing <ArrowRight size={15} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <DesignCTA />
    </>
  );
}
